/**
 * TemplateImportModal.jsx - Template Import Modal Component
 * 
 * Part 5 Checklist Items Covered:
 * - L1: Import Overlay (dark backdrop)
 * - L2: Import Card (content container)
 * - L3: Upload HTML Button (file picker trigger)
 * - L4: Imported File Label (shows filename)
 * - L5: Template Name Input (name field)
 * - L6: Subject Input (subject line field)
 * - L7: Template Type Chips (Custom/Invitation/Save the Date/Reminder)
 * - L8: Status Chips (Ready/Draft toggle)
 * - L9: HTML Textarea (paste HTML content)
 * - L10: Save Import Button (confirm import)
 * - L11: Cancel Button (close without saving)
 */

import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Check, AlertTriangle } from 'lucide-react';
import { COLORS } from './messagingStyles';

// ─── Constants ──────────────────────────────────────────────────────────────────

// L7: Template Type Options
const TEMPLATE_TYPES = [
  { key: 'custom', label: 'Custom' },
  { key: 'invitation', label: 'Invitation' },
  { key: 'save_the_date', label: 'Save the Date' },
  { key: 'reminder', label: 'Reminder' },
  { key: 'rsvp', label: 'RSVP' },
];

// L8: Status Options
const STATUS_OPTIONS = [
  { key: 'ready', label: 'Ready' },
  { key: 'draft', label: 'Draft' },
];

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  // L1: Import Overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 24,
  },
  
  // L2: Import Card
  card: {
    width: '100%',
    maxWidth: 600,
    maxHeight: '90vh',
    borderRadius: 20,
    background: COLORS.surface,
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  
  // Body
  body: {
    flex: 1,
    padding: 24,
    overflowY: 'auto',
  },
  
  // Form
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textSecondary,
    marginBottom: 8,
  },
  formInput: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 14,
    color: COLORS.textPrimary,
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  formInputFocus: {
    borderColor: COLORS.gold,
  },
  
  // L3: Upload HTML Button
  uploadZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    borderRadius: 12,
    border: `2px dashed ${COLORS.borderMedium}`,
    background: COLORS.chipBg,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  uploadZoneHover: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
  },
  uploadIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  uploadText: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  uploadHint: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  
  // L4: Imported File Label
  fileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderRadius: 10,
    background: COLORS.successLight,
    marginTop: 12,
  },
  fileLabelIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileLabelText: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.success,
  },
  fileLabelRemove: {
    width: 24,
    height: 24,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // L7 & L8: Chip selectors
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    padding: '8px 14px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  chipActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
    color: COLORS.goldDark,
  },
  
  // L9: HTML Textarea
  textarea: {
    width: '100%',
    minHeight: 160,
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 13,
    fontFamily: 'Monaco, Menlo, monospace',
    color: COLORS.textPrimary,
    resize: 'vertical',
    outline: 'none',
  },
  
  // Footer
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${COLORS.borderLight}`,
  },
  
  // L11: Cancel Button
  cancelBtn: {
    padding: '12px 20px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    transition: 'all 0.15s',
  },
  
  // L10: Save Import Button
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    borderRadius: 10,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    color: '#FFFFFF',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  saveBtnDisabled: {
    background: '#E4DBC9',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};

// ─── Component ──────────────────────────────────────────────────────────────────

const TemplateImportModal = ({ onClose, onSave }) => {
  // Form state
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [templateType, setTemplateType] = useState('custom');
  const [status, setStatus] = useState('draft');
  const [htmlContent, setHtmlContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [isHovering, setIsHovering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const fileInputRef = useRef(null);

  // Validation
  const isFormValid = name.trim() && subject.trim() && htmlContent.trim();

  // L3: Upload HTML Button handler
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        setHtmlContent(event.target?.result || '');
      };
      reader.readAsText(file);
    }
  };

  const handleRemoveFile = () => {
    setFileName('');
    setHtmlContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // L10: Save handler
  const handleSave = async () => {
    if (!isFormValid) return;
    
    setIsSaving(true);
    try {
      const templateData = {
        name: name.trim(),
        subject: subject.trim(),
        type: templateType,
        status,
        html_content: htmlContent.trim(),
      };
      
      if (onSave) {
        await onSave(templateData);
      }
      
      onClose();
    } catch (err) {
      console.error('Failed to save template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // L1: Import Overlay
    <div style={styles.overlay} onClick={onClose} data-testid="template-import-modal">
      {/* L2: Import Card */}
      <div style={styles.card} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <Upload size={20} color={COLORS.gold} />
            </div>
            <div style={styles.headerTitle}>Import Template</div>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = COLORS.surface;
              e.currentTarget.style.borderColor = COLORS.borderMedium;
            }}
            data-testid="import-close-btn"
          >
            <X size={18} color={COLORS.textSecondary} />
          </button>
        </div>
        
        {/* Body */}
        <div style={styles.body}>
          {/* L3: Upload HTML Button */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Upload HTML File</label>
            <input
              type="file"
              accept=".html,.htm"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
              data-testid="import-file-input"
            />
            <div
              style={{
                ...styles.uploadZone,
                ...(isHovering ? styles.uploadZoneHover : {}),
              }}
              onClick={() => fileInputRef.current?.click()}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              data-testid="import-upload-zone"
            >
              <div style={styles.uploadIcon}>
                <Upload size={24} color={COLORS.gold} />
              </div>
              <div style={styles.uploadText}>Click to upload HTML file</div>
              <div style={styles.uploadHint}>or drag and drop</div>
            </div>
            
            {/* L4: Imported File Label */}
            {fileName && (
              <div style={styles.fileLabel} data-testid="import-file-label">
                <div style={styles.fileLabelIcon}>
                  <FileText size={16} color={COLORS.success} />
                </div>
                <span style={styles.fileLabelText}>{fileName}</span>
                <button
                  style={styles.fileLabelRemove}
                  onClick={handleRemoveFile}
                >
                  <X size={14} color={COLORS.success} />
                </button>
              </div>
            )}
          </div>
          
          {/* L5: Template Name Input */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Wedding Invitation"
              style={styles.formInput}
              data-testid="import-name-input"
            />
          </div>
          
          {/* L6: Subject Input */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Subject Line *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., You're Invited to Our Special Day"
              style={styles.formInput}
              data-testid="import-subject-input"
            />
          </div>
          
          {/* L7: Template Type Chips */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Template Type</label>
            <div style={styles.chipRow}>
              {TEMPLATE_TYPES.map(type => (
                <button
                  key={type.key}
                  style={{
                    ...styles.chip,
                    ...(templateType === type.key ? styles.chipActive : {}),
                  }}
                  onClick={() => setTemplateType(type.key)}
                  type="button"
                  data-testid={`import-type-${type.key}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* L8: Status Chips */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Status</label>
            <div style={styles.chipRow}>
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  style={{
                    ...styles.chip,
                    ...(status === opt.key ? styles.chipActive : {}),
                  }}
                  onClick={() => setStatus(opt.key)}
                  type="button"
                  data-testid={`import-status-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* L9: HTML Textarea */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>HTML Content *</label>
            <textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              placeholder="Paste your HTML content here or upload a file above..."
              style={styles.textarea}
              data-testid="import-html-textarea"
            />
          </div>
        </div>
        
        {/* Footer */}
        <div style={styles.footer}>
          {/* L11: Cancel Button */}
          <button
            style={styles.cancelBtn}
            onClick={onClose}
            data-testid="import-cancel-btn"
          >
            Cancel
          </button>
          
          {/* L10: Save Import Button */}
          <button
            style={{
              ...styles.saveBtn,
              ...(!isFormValid || isSaving ? styles.saveBtnDisabled : {}),
            }}
            onClick={handleSave}
            disabled={!isFormValid || isSaving}
            onMouseEnter={(e) => {
              if (isFormValid && !isSaving) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.35)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,167,90,0.25)';
            }}
            data-testid="import-save-btn"
          >
            <Check size={16} />
            {isSaving ? 'Saving...' : 'Import Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateImportModal;
